import React, { PropsWithChildren, useEffect, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import { FilterEventHandler, IBootstrapTableColumnProps } from 'react-bootstrap-table-next';
import { ICustomDateRangeFilterProps, TCustomDateRangeFilterValue } from 'react-bootstrap-table2-filter';
import styled from 'styled-components';
import { DateTime } from 'luxon';
import { Card } from '../Card';
import { Button } from '../Button';
import { Grid } from '../Grid';
import { Calendar } from '../Calendar';
import { useDelayedOnFilterEffect } from './useDelayedOnFilterEffect';
import { DateRangeDropdown } from './DateRangeDropdown';
import { faCaretDown } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { IPosition, positionStyles } from '../shared/IPosition';
import { ModalSubstrate } from '../ModalWindow';
import { mergeTests } from '../../test-utilities/mergeTests';

const CALENDAR_WIDTH = 1 + 16 + 231 + 16 + 231 + 16 + 1; // 2 calendars @ 231px + 3 padding/gutters @ 16px + a border
const DATE_RANGE_DROPDOWN_HEIGHT = 154 + 8; // dropdown with 4 elements + spacing5

const FilterDiv = styled.div`
  position: relative;
`;

const FloatingPanel = styled.div<{ top: number; left?: number; right?: number; fixed: boolean }>`
  position: ${(props) => (props.fixed ? 'fixed' : 'absolute')};
  ${(props) => (props.left ? `left: ${props.left}px;` : '')};
  ${(props) => (props.right ? `right: ${props.right}px;` : '')};
  top: ${(props) => props.top}px;
  z-index: 300;
`;

const StyledCard = styled(Card)<{ position: IPosition }>`
  ${positionStyles};
  border-radius: ${(props) => props.theme.spacing.spacing2};
  padding: ${(props) => props.theme.spacing.spacing5};
  width: ${CALENDAR_WIDTH}px;
`;

const RoomyGrid = styled(Grid)`
  gap: ${(props) => props.theme.spacing.spacing5};
`;

const CurrentDateButton = styled(Button)`
  align-items: center;
  display: grid;
  gap: ${(props) => props.theme.spacing.spacing5};
  grid-auto-flow: column;
`;

const ButtonGrid = styled.div`
  display: grid;
  justify-content: end;
  gap: ${(props) => props.theme.spacing.spacing5};
  grid-auto-flow: column;
  grid-auto-columns: max-content;
`;

export interface IDateRangeFilterProps extends Omit<ICustomDateRangeFilterProps, 'type'> {
  onFilter: FilterEventHandler;
  showClear?: boolean;
  fixed?: boolean;
}

// FYI: presets are generically useful so implementation of preset support is all the way down here in DataGrid
const convertPresetToDate = (preset: string): string => {
  const presetNumber = preset.match(/^\d+$/) ? parseInt(preset) : null;
  return presetNumber !== null && !isNaN(presetNumber)
    ? DateTime.local().minus({ days: presetNumber }).toISODate()
    : preset;
};

const convertDefaultValueToValue = (defaultValue: TCustomDateRangeFilterValue): [string, string] =>
  // if defaultValue is array of 2 strings it is assumed that it is in ISO Date format

  defaultValue.length === 1 ? [convertPresetToDate(defaultValue[0]), DateTime.local().toISODate()] : defaultValue;

export const DateRangeFilter = ({
  defaultValue,
  onFilter,
  delay,
  showClear,
  fixed = false,
}: PropsWithChildren<IDateRangeFilterProps>) => {
  const [defaultStartDate, defaultEndDate] = defaultValue ? convertDefaultValueToValue(defaultValue) : [null, null];
  const [value, setValue] = useState<[string | null, string | null] | undefined>([defaultStartDate, defaultEndDate]);
  const [startDate, endDate] = value || [null, null];
  const [unsavedValue, setUnsavedValue] = useState<string[]>(
    !!startDate && !!endDate ? [startDate, endDate] : !!startDate ? [startDate] : []
  );

  //reset value if default is changed
  useEffect(() => setValue([defaultStartDate, defaultEndDate]), [defaultStartDate, defaultEndDate]);

  useDelayedOnFilterEffect(startDate && endDate ? value : undefined, delay, onFilter);

  // Button + Calendar Popup Logic
  const buttonRef = useRef<HTMLButtonElement>(null);
  const [showDropdown, setShowDropdown] = useState(false);
  const [showCalendar, setShowCalendar] = useState(false);
  const floatingPanelRef = useRef<HTMLDivElement>(null);
  const {
    bottom: anchorY,
    left,
    right,
  } = buttonRef.current
    ? buttonRef.current.getBoundingClientRect()
    : { bottom: 0, left: 0, right: document.body.offsetWidth };
  const anchorOnLeft = document.body.offsetWidth > left + CALENDAR_WIDTH;
  const anchorX = anchorOnLeft ? left : document.body.offsetWidth - right;

  const onClickOutside = () => {
    setShowCalendar(false);
    setShowDropdown(false);
  };

  useEffect(() => {
    if (showCalendar) setUnsavedValue(!!startDate && !!endDate ? [startDate, endDate] : !!startDate ? [startDate] : []);
    else setUnsavedValue([]);
  }, [startDate, endDate, showCalendar]);

  return (
    <FilterDiv>
      <CurrentDateButton
        intent={'ghost'}
        isInline
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          setShowDropdown(true);
        }}
        data-test-class={mergeTests('button', 'show-dropdown')}
        ref={buttonRef}
      >
        {startDate && endDate ? (
          startDate === endDate ? (
            DateTime.fromISO(startDate).toFormat('L/d')
          ) : (
            <>
              {DateTime.fromISO(startDate).toFormat('L/d')}
              {' - '}
              {DateTime.fromISO(endDate).toFormat('L/d')}
            </>
          )
        ) : (
          'Select Date'
        )}
        <FontAwesomeIcon icon={faCaretDown} />
      </CurrentDateButton>
      {(showDropdown || showCalendar) &&
        // FYI: we are using Portal here so we can avoid useSortEventListenerFix like in the text filter
        ReactDOM.createPortal(
          <>
            <ModalSubstrate onClick={onClickOutside} />
            <FloatingPanel
              left={anchorOnLeft ? anchorX : undefined}
              onClick={(e) => e.stopPropagation()}
              ref={floatingPanelRef}
              right={!anchorOnLeft ? anchorX : undefined}
              top={anchorY}
              fixed={fixed}
            >
              {showDropdown && (
                <DateRangeDropdown
                  date={
                    showCalendar
                      ? (['CUSTOM', 'CUSTOM'] as [string, string])
                      : !!value && value.length
                        ? (value as [string, string])
                        : undefined
                  }
                  onChange={(date) => {
                    if (date[0] === 'CUSTOM' && date[1] === 'CUSTOM') {
                      setUnsavedValue([]);
                      setShowCalendar(true);
                    } else {
                      setValue(date);
                      setShowDropdown(false);
                      setShowCalendar(false);
                    }
                  }}
                  position={{
                    anchor: anchorOnLeft ? 'top-left' : 'top-right',
                    x: 0,
                    y: 0,
                  }}
                />
              )}
              {showCalendar && (
                <StyledCard
                  position={{
                    anchor: anchorOnLeft ? 'top-left' : 'top-right',
                    x: 0,
                    y: DATE_RANGE_DROPDOWN_HEIGHT,
                  }}
                >
                  <RoomyGrid>
                    <Calendar
                      inCard={false}
                      inputDate={unsavedValue}
                      onChange={(newDate) => {
                        // do nothing if the change is to null
                        if (!!newDate.value) {
                          // if the first date is already selected
                          if (!!unsavedValue[0] && !unsavedValue[1])
                            //using simple string sort
                            setUnsavedValue((prevState) => [prevState[0], newDate.value!].sort());
                          else setUnsavedValue([newDate.value!]);
                        }
                      }}
                      selectionMode="range"
                      showExtraMonths="previous"
                      monthControlType={'control'}
                      yearControlType={'control'}
                    />
                    {showClear && (
                      <ButtonGrid>
                        <Button
                          disabled={unsavedValue.length === 0}
                          intent="danger"
                          isInline
                          onClick={() => {
                            setUnsavedValue([]);
                          }}
                          type="button"
                        >
                          Clear
                        </Button>
                        <Button
                          disabled={unsavedValue.length === 1}
                          intent="ghost"
                          isInline
                          onClick={() => {
                            setValue(unsavedValue.length ? (unsavedValue as [string, string]) : [null, null]);
                            setShowDropdown(false);
                            setShowCalendar(false);
                          }}
                          type="button"
                        >
                          Save
                        </Button>
                      </ButtonGrid>
                    )}
                  </RoomyGrid>
                </StyledCard>
              )}
            </FloatingPanel>
          </>,
          document.body
        )}
    </FilterDiv>
  );
};

export interface IDateRangeFilterRendererProps extends Omit<ICustomDateRangeFilterProps, 'type'> {}

export const DateRangeFilterRenderer =
  <T extends object>(config: IDateRangeFilterRendererProps) =>
  (onFilter: FilterEventHandler, column: IBootstrapTableColumnProps<T>) => (
    <DateRangeFilter {...config} onFilter={onFilter} />
  );
